import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {Text} from '@Components/text';
import {noop} from '@Utils/general.util';
import type {TabItemContentStorage, TabKey} from '@Components/tabs';
import {Tabs} from '@Components/tabs';
import type {PosterPageData} from '@Libraries/poster-library';
import {loadDesignSelector} from '@Libraries/poster-library';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {DesignGallery} from '@Components/design-gallery';
import {selectTab} from '@Components/tabs/tabs-reducer';
import type {DesignSelectorProps} from '@Components/design-selector/design-selector.types';
import {containerQuery, DesignSelectorTab, skeletonLoaderContainerQuery} from '@Components/design-selector/design-selector.types';
import {GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import {SkeletonBrick} from '../skeleton-brick';
import styles from './design-selector.module.scss';
import {useAppDispatch} from '@/hooks';
import '@Global/create-from-scratch';
import type {DesignSelection} from '@Components/social-media/social-media.types';

export function DesignSelector({
  onDesignsSelection = (_designsSelected: DesignSelection[]): void => {},
  initialDesignSelections = [],
  containerRef,
  scrollContainerRef,
  onGalleryTemplateSelectionCallback = (_designs: DesignSelection[]): void => {},
  onTabSelected = noop,
  designSelectorTab = DesignSelectorTab.MY_DESIGNS,
  startDesignFromMediaModalBtnOpts = undefined,
  isLoading = false,
  designGalleryStartingURI = GalleryThemeURI.DEFAULT,
  hideTabContainer = false,
  desktopClasses = '',
  mobileClasses = '',
  designGalleryHeight = '80vh',
  maxSelection,
}: DesignSelectorProps): ReactElement<any> {
  const isUserPAYG = window.PMW.isPAYGUser();
  const SKELETON_LOADER = 'js-design-selector-element-skeleton-loader';
  const DESIGN_SELECTOR_ELEMENT_TABS_ID = 'design-selector-element-tabs';
  const LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT = 'loadDesignsForDesignSelectorIfNotLoaded';
  const params = useContainerQuery(containerRef, containerQuery);
  const skeletonParams = useContainerQuery(containerRef, skeletonLoaderContainerQuery);
  const paramsRef = useRef(params);
  const [areMyDesignsLoaded, setAreMyDesignsLoaded] = useState<boolean>(false);
  const [myDesignsLength, setMyDesignsLength] = useState<number>(0);
  const selectedDesignsRef = useRef<DesignSelection[]>(initialDesignSelections);

  const [areSharedWithMeDesignsLoaded, setAreSharedWithMeDesignsLoaded] = useState(false);
  const dispatch = useAppDispatch();

  let skeletonLoaderHTML = document.querySelector(`.${SKELETON_LOADER}`);

  const isMyDesignsSelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.MY_DESIGNS;
  };

  const updateTabSelection = (newTab: DesignSelectorTab): void => {
    onTabSelected(newTab);
  };

  const isTeamSelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.SHARED_WITH_ME;
  };

  const isDesignGallerySelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES;
  };

  useEffect(() => {
    selectedDesignsRef.current.forEach((design) => {
      removeStylesOnDeselection(design);
    });

    initialDesignSelections?.forEach((design) => {
      addStylesOnSelection(design);
    });
    selectedDesignsRef.current = initialDesignSelections;
  }, [initialDesignSelections]);

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const getLoaderHTML = (): Element | null => {
    if (skeletonLoaderHTML) {
      return skeletonLoaderHTML;
    }

    skeletonLoaderHTML = document.querySelector(`.${SKELETON_LOADER}`);
    return skeletonLoaderHTML;
  };

  const addSelectionStylesIfNeeded = (): void => {
    initialDesignSelections?.forEach((design) => {
      addStylesOnSelection(design);
    });
  };

  useEffect(() => {
    if (!areMyDesignsLoaded) {
      loadDesignSelector(
        () => {
          hideLoader();
          setAreMyDesignsLoaded(true);
          setMyDesignsLength(getMyDesignsLength());
          addSelectionStylesIfNeeded();
        },
        onDesignsClicked,
        '.js-design-selector-element',
        LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT,
        startDesignFromMediaModalBtnOpts,
        initialDesignSelections,
        scrollContainerRef?.current ?? null
      );
    }
  }, []);

  useEffect(() => {
    const handleLazyLoadedDesigns = (event: Event): void => {
      const customEvent = event as CustomEvent<{posterIds: string[]}>;
      const {posterIds} = customEvent.detail;

      // selectedDesignsRef.current is the single source of truth; the vanilla event payload's
      // preselectedDesigns only reflects the initial prop, not in-session selections.
      selectedDesignsRef.current.forEach((design) => {
        if (posterIds.includes(design.posterHashedId)) {
          addStylesOnSelection(design);
        }
      });
    };

    document.addEventListener('designs-lazy-loaded', handleLazyLoadedDesigns);

    return (): void => {
      document.removeEventListener('designs-lazy-loaded', handleLazyLoadedDesigns);
    };
  }, []);

  const shouldShowLoader = (): boolean => {
    if (isMyDesignsSelected() && !areMyDesignsLoaded) {
      return true;
    }

    return isTeamSelected() && !areSharedWithMeDesignsLoaded;
  };

  const showLoader = (): void => {
    getLoaderHTML()?.classList.remove('_hidden');
  };

  const hideLoader = (): void => {
    getLoaderHTML()?.classList.add('_hidden');
  };

  const isLoaderVisible = (): boolean => {
    return !getLoaderHTML()?.classList.contains('_hidden');
  };

  useDidUpdateEffect(() => {
    dispatch(selectTab(DESIGN_SELECTOR_ELEMENT_TABS_ID, designSelectorTab));

    if (!shouldShowLoader() && isLoaderVisible()) {
      hideLoader();
    }

    if (shouldShowLoader() && !isDesignGallerySelected()) {
      showLoader();

      window.dispatchEvent(
        new CustomEvent(LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT, {
          detail: {
            onDesignLoadedCallback: (): void => {
              getLoaderHTML()?.classList.add('_hidden');
              if (isMyDesignsSelected()) {
                setAreMyDesignsLoaded(true);
              } else if (isTeamSelected()) {
                setAreSharedWithMeDesignsLoaded(true);
              }
            },
          },
        })
      );
    }

    if (shouldShowLoader() && isDesignGallerySelected()) {
      hideLoader();
    }
  }, [designSelectorTab]);

  useEffect(() => {
    dispatch(selectTab(DESIGN_SELECTOR_ELEMENT_TABS_ID, designSelectorTab));
  }, []);

  const getTabItems = (): TabItemContentStorage[] => {
    return [
      {
        tabKey: DesignSelectorTab.MY_DESIGNS,
        text: window.i18next.t('pmwjs_my_designs'),
      },
      {
        tabKey: DesignSelectorTab.SHARED_WITH_ME,
        text: window.i18next.t('pmwjs_shared_with_me'),
      },
      {
        tabKey: DesignSelectorTab.GALLERY_TEMPLATES,
        text: window.i18next.t('pmwjs_design_templates'),
      },
    ];
  };

  const isDesignAlreadySelected = (clickedDesign: PosterPageData): boolean => {
    return selectedDesignsRef.current.some((selectedDesign) => {
      return selectedDesign.posterHashedId === clickedDesign.posterId && selectedDesign.pageHashedId === clickedDesign.pageId;
    });
  };

  const getDesignsToSelect = (clickedDesigns: PosterPageData[]): DesignSelection[] => {
    return clickedDesigns
      .map((clickedDesign) => {
        if (!isDesignAlreadySelected(clickedDesign)) {
          return {posterHashedId: clickedDesign.posterId, pageHashedId: clickedDesign.pageId} as DesignSelection;
        }
        return undefined;
      })
      .filter((design): design is DesignSelection => !!design);
  };

  const getDesignsToUnselect = (clickedDesigns: PosterPageData[]): DesignSelection[] => {
    return clickedDesigns
      .map((clickedDesign) => {
        if (isDesignAlreadySelected(clickedDesign)) {
          return {posterHashedId: clickedDesign.posterId, pageHashedId: clickedDesign.pageId} as DesignSelection;
        }
        return undefined;
      })
      .filter((design): design is DesignSelection => !!design);
  };

  const isMobile = (): boolean => {
    return !!paramsRef.current?.small;
  };

  const getMyDesignsLength = (): number => {
    return document.querySelectorAll('[data-id]').length;
  };

  const removeDefaultStyles = (designId: string): void => {
    const element = document.querySelector(`[data-id="${designId}"]`);

    if (!element) {
      return;
    }

    if (isMobile()) {
      element.classList.add(styles.posterItemMobile);
    } else {
      element.classList.add(styles.posterItem);
    }
  };

  const removeSelectionOverlayIfNeeded = (designId: string): void => {
    if (!isMobile()) {
      return;
    }

    const overlayElement = $(`[overlay-selection="${designId}"]`);
    const previewContainer = $(`[data-id="${designId}"]`).children().eq(0);

    previewContainer.removeClass(styles.overlayWrapperClasses);
    overlayElement.remove();
  };

  const addStylesOnSelection = (design: DesignSelection): void => {
    const {posterHashedId, pageHashedId} = design;
    const element = $(`[data-id="${posterHashedId}"]`);
    const elementPage = $(`[data-id="${posterHashedId}"] [data-page-id="${pageHashedId}"]`);
    $(`[data-id="${posterHashedId}"] .preview-container-multipages`).eq(0);
    const nameInsideElement = $(`[data-id="${posterHashedId}"] .name`).eq(0);

    removeDefaultStyles(posterHashedId);

    const checkIcon = '<i class="icon-check size-icon-16"></i>';
    const newDiv = $('<div>').html(checkIcon).attr('selected-id', posterHashedId);
    newDiv.addClass(`${styles.numberTag} ${isMobile() ? styles.small : ''}`);
    element.append(newDiv);

    elementPage.addClass('-selected');
    nameInsideElement.addClass(styles.selectedName);

    if (!window.PMW.enableMyStuffMultipagesUI) {
      element.addClass('selected-item');
    }
  };

  const removeStylesOnDeselection = (design: DesignSelection): void => {
    const {posterHashedId, pageHashedId} = design;
    const element = $(`[data-id="${posterHashedId}"]`);
    const previewInsideElement = $(`[data-id="${posterHashedId}"] .preview-container-multipages`).eq(0);
    const nameInsideElement = $(`[data-id="${posterHashedId}"] .name`).eq(0);
    const selectorElement = $(`[selected-id="${posterHashedId}"]`);
    const elementPage = $(`[data-id="${posterHashedId}"] [data-page-id="${pageHashedId}"]`);
    const checkElement = $(`[data-selected-check="${posterHashedId}"]`);

    removeDefaultStyles(posterHashedId);

    previewInsideElement.removeClass(styles.selectedClass);
    nameInsideElement.removeClass(styles.selectedName);
    elementPage.removeClass('-selected');
    selectorElement.remove();
    checkElement.remove();

    if (!window.PMW.enableMyStuffMultipagesUI) {
      element.removeClass('selected-item');
    }

    removeSelectionOverlayIfNeeded(posterHashedId);

    if (!window.PMW.enableMyStuffMultipagesUI) {
      const element = $(`[data-id="${posterHashedId}"]`);
      element.removeClass('selected-item');
    }
  };

  const onDesignsClicked = (designsClicked: PosterPageData[]): void => {
    if (designsClicked.length === 0) {
      return;
    }

    const designsToSelect = getDesignsToSelect(designsClicked);
    const designsToUnselect = getDesignsToUnselect(designsClicked);

    let newSelectedDesigns = [
      ...selectedDesignsRef.current.filter(
        (design) => !designsToUnselect.some((toUnselect) => toUnselect.posterHashedId === design.posterHashedId && toUnselect.pageHashedId === design.pageHashedId)
      ),
      ...designsToSelect,
    ];

    if (maxSelection !== undefined && newSelectedDesigns.length > maxSelection) {
      const itemsToRemoveCount = newSelectedDesigns.length - maxSelection;
      const itemsToRemove = newSelectedDesigns.slice(0, itemsToRemoveCount);

      itemsToRemove.forEach((design) => {
        if (design) {
          removeStylesOnDeselection(design);
        }
      });

      newSelectedDesigns = newSelectedDesigns.slice(itemsToRemoveCount);
    }

    selectedDesignsRef.current = newSelectedDesigns;

    selectedDesignsRef.current.forEach((design) => {
      addStylesOnSelection(design);
    });

    designsToUnselect.forEach((design) => {
      removeStylesOnDeselection(design);
    });

    onDesignsSelection(selectedDesignsRef.current);
  };

  const onTabClick = (tabKey: TabKey): void => {
    if ((tabKey as DesignSelectorTab) === DesignSelectorTab.MY_DESIGNS) {
      updateTabSelection(DesignSelectorTab.MY_DESIGNS);
    } else if ((tabKey as DesignSelectorTab) === DesignSelectorTab.GALLERY_TEMPLATES) {
      updateTabSelection(DesignSelectorTab.GALLERY_TEMPLATES);
    } else if (isUserPAYG) {
      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_TEAMS as string, noop, true);
    } else {
      updateTabSelection(DesignSelectorTab.SHARED_WITH_ME);
    }
  };

  const getClassesForDesktopUpperContainer = (): string => {
    return `${styles.upperContainerDesktop} ${isDesignGallerySelected() ? 'spacing-m-b-0' : ''}`;
  };

  const getDesktopUpperContainer = (): ReactElement<any> => {
    return (
      <div id="design-selector-onboarding-container" className={getClassesForDesktopUpperContainer()}>
        <Tabs className="spacing-m-b-4" id={DESIGN_SELECTOR_ELEMENT_TABS_ID} tabItems={getTabItems()} selectedTab={designSelectorTab} onTabClick={onTabClick} />
      </div>
    );
  };

  const getDropdownText = (): string => {
    if (isMyDesignsSelected()) {
      return window.i18next.t('pmwjs_my_designs');
    }

    if (isTeamSelected()) {
      return window.i18next.t('pmwjs_team');
    }

    return window.i18next.t('pmwjs_design_gallery');
  };

  const getClassesForMobileUpperContainer = (): string => {
    return `${styles.upperContainerMobile} ${isDesignGallerySelected() ? 'spacing-m-b-0' : ''}`;
  };

  const getMobileUpperContainer = (): ReactElement<any> => {
    return (
      <div className={getClassesForMobileUpperContainer()}>
        <Text val={window.i18next.t('pmwjs_select_a_design_lowercase')} className={`${styles.selectADesignTextMobile} _unmargin}`} />
        <Dropdown
          className={styles.designSelectorMobileDropdown}
          selector={<DropdownDefaultSelectBox id="design-selector-dropdown-1" text={getDropdownText()} size={DROPDOWN_SIZE.SMALL} isLoading={false} />}
          popup={
            <div style={{width: '100%'}}>
              <ControlledList
                id="design-selector-dropdown-list"
                items={[
                  {
                    id: 'design-selector-dropdown-2',
                    text: window.i18next.t('pmwjs_my_designs'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    isSelected: isMyDesignsSelected(),
                    onClick: (): void => {
                      updateTabSelection(DesignSelectorTab.MY_DESIGNS);
                    },
                    showSelectionCheckmark: false,
                  },
                  {
                    id: 'design-selector-dropdown-3',
                    text: window.i18next.t('pmwjs_team'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    onClick: (): void => {
                      updateTabSelection(DesignSelectorTab.SHARED_WITH_ME);
                    },
                    showSelectionCheckmark: false,
                  },
                  {
                    id: 'design-selector-dropdown-4',
                    text: window.i18next.t('pmwjs_design_gallery'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    onClick: (): void => {
                      updateTabSelection(DesignSelectorTab.GALLERY_TEMPLATES);
                    },
                    showSelectionCheckmark: false,
                  },
                ]}
              />
            </div>
          }
        />
      </div>
    );
  };

  const getUpperContainer = (): ReactElement<any> | null => {
    if (hideTabContainer) {
      return null;
    }

    return params.small ? getMobileUpperContainer() : getDesktopUpperContainer();
  };

  const getFolderLoader = (): ReactElement<any> => {
    let folderLoaderCount = 0;
    let rightMarginString = '';

    if (skeletonParams.xxsmall) {
      folderLoaderCount = 1;
    } else if (skeletonParams.xsmall) {
      folderLoaderCount = 2;
      rightMarginString = 'spacing-m-r-3';
    } else if (skeletonParams.small) {
      folderLoaderCount = 3;
      rightMarginString = 'spacing-m-r-4';
    } else if (skeletonParams.medium || skeletonParams.large) {
      folderLoaderCount = 2;
      rightMarginString = 'spacing-m-r-5';
    } else if (skeletonParams.xlarge || skeletonParams.xxlarge) {
      folderLoaderCount = 3;
      rightMarginString = 'spacing-m-r-5';
    } else if (skeletonParams.xxxlarge) {
      folderLoaderCount = 4;
      rightMarginString = 'spacing-m-r-6';
    }
    const skeletonArray: ReactElement<any>[] = [];
    for (let i = 0; i < folderLoaderCount; i++) {
      const isLastFolder = i === folderLoaderCount - 1;
      skeletonArray.push(<SkeletonBrick isPulsating className={`radius-2 ${isLastFolder ? '' : rightMarginString}`} height={56} key={`design-selector-skeleton-${i}`} />);
    }
    return <div className={`spacing-m-b-3 flex-h-row ${styles.foldersSkeleton}`}>{skeletonArray}</div>;
  };

  const getPosterLoader = (): ReactElement<any> => {
    let posterLoaderCount = 0;
    let rightMarginString = '';
    if (skeletonParams.xxsmall) {
      posterLoaderCount = 1;
    } else if (skeletonParams.xsmall || skeletonParams.small) {
      posterLoaderCount = 3;
      rightMarginString = 'spacing-m-r-5';
    } else if (skeletonParams.medium) {
      posterLoaderCount = 2;
      rightMarginString = 'spacing-m-r-6';
    } else if (skeletonParams.large || skeletonParams.xlarge) {
      posterLoaderCount = 3;
      rightMarginString = 'spacing-m-r-6';
    } else if (skeletonParams.xxlarge || skeletonParams.xxxlarge) {
      posterLoaderCount = 4;
      rightMarginString = 'spacing-m-r-5';
    }

    const skeletonArray: ReactElement<any>[] = [];
    for (let i = 0; i < posterLoaderCount; i++) {
      const isLastPoster = i === posterLoaderCount - 1;

      skeletonArray.push(
        <div className={isLastPoster ? '' : rightMarginString} style={{width: '100%'}} key={`design-selector-poster-skeleton-${i}`}>
          <SkeletonBrick isPulsating className="spacing-m-b-4" height={148} />
          <SkeletonBrick isPulsating height={20} width={136} />
        </div>
      );
    }
    return <div className="flex-h-row">{skeletonArray}</div>;
  };

  const getClassesForLoaderWrapper = (): string => {
    return `${styles.loaderWrapperMystuff} ${isDesignGallerySelected() ? '_hidden' : ''}`;
  };

  const getLoader = (): ReactElement<any> => {
    return (
      <div className={`${getClassesForLoaderWrapper()} ${SKELETON_LOADER}`}>
        <div className={styles.designSelectorLoaderDesktop}>
          {getFolderLoader()}
          <div className={styles.viewMoreButtonSkeletonContainer}>
            <SkeletonBrick isPulsating className={`radius-4 ${styles.viewMoreButtonSkeleton}`} width={80} height={20} />
          </div>
          {getPosterLoader()}
        </div>
      </div>
    );
  };

  const getDesignSelectorLoadingClass = (): string => {
    if (isLoading) {
      return styles.isLoadingDesignSelector;
    }

    return '';
  };

  const getDesignSelectorDesktopClasses = (): string => {
    return `${styles.designSelectorDesktop} ${desktopClasses}`;
  };

  const getDesignSelectorMobileClasses = (): string => {
    return `${styles.designSelectorMobile} ${mobileClasses} ${window.PMW.enableMyStuffMultipagesUI ? 'js-pages-container' : ''}`;
  };

  const getDesignSelectorClasses = (): string => {
    let classes = getDesignSelectorLoadingClass();

    if (params.small) {
      classes += ` ${getDesignSelectorMobileClasses()}`;
    } else {
      classes += ` ${getDesignSelectorDesktopClasses()}`;
    }

    if (isDesignGallerySelected()) {
      classes += ` ${styles.templateGallerySelector}`;
    }

    return classes;
  };

  const getDesignSelector = (): ReactElement<any> => {
    return (
      <div className={getDesignSelectorClasses()}>
        {getUpperContainer()}
        {getLoader()}
        <div className={`js-design-selector-element design-selector-element ${styles.designSelectorElementContent} ${isMyDesignsSelected() || isTeamSelected() ? '' : '_hidden'}`}>
          <div className={`mystuff-posters ${isMyDesignsSelected() ? 'selected' : '_hidden'} ${myDesignsLength ? '' : styles.emptyMyDesigns}`} />
          <div className={`shared-with-me-posters ${isTeamSelected() ? 'selected' : '_hidden'}`} />
        </div>

        <div className={`${styles.galleryTemplateSelector} ${isDesignGallerySelected() ? '' : '_hidden'}`}>
          <DesignGallery
            disableCreateFromScratchCTA
            onDesignsSelected={onGalleryTemplateSelectionCallback}
            height={designGalleryHeight}
            startingPageURI={designGalleryStartingURI}
          />
        </div>
      </div>
    );
  };

  return <div ref={containerRef}>{getDesignSelector()}</div>;
}
